/**
 * User switcher for the local Dev Panel: a select-with-typeahead combobox filtered by name, email, or role.
 */
import * as React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { filterUsersBySearch } from 'shared/src/utils/user/user-utils';
import { UserRoleBadges } from '@/components/display/role-badges';

export type SwitcherUser = {
  id: string;
  name: string;
  email: string;
  roles: string[];
};

// Cap the unfiltered list; there are ~360 users.
const MAX_ROWS = 200;

function UserRow({
  user,
  highlighted,
  onSelect,
  onHover
}: {
  user: SwitcherUser;
  highlighted: boolean;
  onSelect: () => void;
  onHover: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      onMouseEnter={onHover}
      className={`flex w-full items-center gap-2 px-2 py-1.5 text-left ${
        highlighted ? 'bg-white/10' : ''
      }`}>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm text-slate-100">{user.name}</span>
        <span className="truncate text-[11px] text-slate-400">{user.email}</span>
      </span>
      <UserRoleBadges roles={user.roles} theme="dark" />
    </button>
  );
}

export function UserSwitcher({
  users,
  isLoading,
  onSelect
}: {
  users: SwitcherUser[];
  isLoading: boolean;
  onSelect: (userId: string) => void;
}) {
  const [query, setQuery] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [highlight, setHighlight] = React.useState(0);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);

  const filtered = React.useMemo(
    () => filterUsersBySearch(users, query).slice(0, MAX_ROWS),
    [users, query]
  );

  React.useEffect(() => setHighlight(0), [query]);

  React.useEffect(() => {
    if (!open) return;
    const el = listRef.current?.children[highlight] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  React.useEffect(() => {
    function onDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  function choose(user: SwitcherUser) {
    setOpen(false);
    setQuery('');
    onSelect(user.id);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      setOpen(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlight(h => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const user = filtered[highlight];
      if (user) choose(user);
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          type="text"
          value={query}
          placeholder={isLoading ? 'Loading users…' : 'Select or search a user…'}
          onChange={e => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onKeyDown={onKeyDown}
          className="w-full rounded-md border border-slate-600 bg-slate-800 px-2 py-1.5 pr-7 text-sm text-slate-100 placeholder:text-slate-500 focus:border-amber-400 focus:outline-none"
        />
        <FontAwesomeIcon
          icon={faChevronDown}
          className="pointer-events-none absolute right-2 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-400"
        />
      </div>

      {open ? (
        // Open upward: the panel sits near the viewport bottom.
        <div
          ref={listRef}
          className="absolute bottom-full z-50 mb-1 max-h-72 w-full overflow-y-auto rounded-md border border-slate-700 bg-slate-900 shadow-xl">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-slate-400">
              {isLoading ? 'Loading…' : 'No users'}
            </div>
          ) : (
            filtered.map((user, i) => (
              <UserRow
                key={user.id}
                user={user}
                highlighted={i === highlight}
                onSelect={() => choose(user)}
                onHover={() => setHighlight(i)}
              />
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
